// Pure reducer for the unread-message provider. Lifted out of the React
// component so it's unit-testable without a renderer.
//
// State model: the server-fetched summary is the source of truth for what
// _was_ unread on this navigation. The `read` set tracks orderIds the user
// has seen during the current session — applied as a subtraction on top of
// the server view. On rehydrate (next navigation), the server summary is
// fresh and the read set is reset to empty.

import type {
  OrderUnreadSummary,
  OrderUnreadSummaryEntry,
} from '@lib/data/order-consultations';

export type UnreadState = {
  readonly summary: OrderUnreadSummary;
  readonly readOrderIds: ReadonlySet<string>;
};

export type UnreadAction =
  | { readonly type: 'mark_read'; readonly orderId: string }
  | { readonly type: 'rehydrate'; readonly summary: OrderUnreadSummary };

export const initialState = (summary: OrderUnreadSummary): UnreadState => {
  return { summary, readOrderIds: new Set() };
};

export const unreadReducer = (
  state: UnreadState,
  action: UnreadAction,
): UnreadState => {
  if (action.type === 'mark_read') {
    if (state.readOrderIds.has(action.orderId)) {
      return state;
    }
    const next = new Set(state.readOrderIds);
    next.add(action.orderId);
    return { ...state, readOrderIds: next };
  }
  // 'rehydrate'
  return { summary: action.summary, readOrderIds: new Set() };
};

export type DerivedUnread = {
  readonly totalUnread: number;
  readonly visibleEntries: readonly OrderUnreadSummaryEntry[];
};

export const deriveUnread = (state: UnreadState): DerivedUnread => {
  const visibleEntries: OrderUnreadSummaryEntry[] = [];
  let totalUnread = 0;
  for (const entry of state.summary.perOrder) {
    if (state.readOrderIds.has(entry.orderId)) {
      continue;
    }
    visibleEntries.push(entry);
    totalUnread += entry.unreadCount;
  }
  return { totalUnread, visibleEntries };
};
